/**
 * External Imports
 */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { uniq, orderBy } from 'lodash';

/**
 * Nexus Imports
 */
import {
  NeuCard,
  NeuIcon,
  NeuButton,
  NeuList,
  NeuCheckbox
} from '@neutron/react';

/**
 * Internal Imports
 */
import QuestionPart from './questionPart/QuestionPart';
import { BlueChip } from '../../shared/chips/blueChip';
import { Dropdown } from '../../shared/dropdowns';
import { qTags } from './QuestionTags';

import { RootState } from '../../../redux/store';
import {
  addNewQuestion,
  resetEditQuestion,
  updateQuestion
} from '../../../redux/actions';
import { handleModal } from '../../../services/ModalPortal/utils';
import { formatRoundingType } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  ConditionListProp,
  Question,
  QuestionPartProp
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  AddQuestionCard,
  AddQuestionPartCard,
  CardContentContainer,
  CardHeader,
  InputHeading,
  ModalCardContent,
  ModalFooter,
  QuestionTitleInput,
  RoundingTypeDropdownContainer,
  StyledLabel
} from './AddQuestionModal.styles';
import { QuestionPartTypeListItem } from './AddQuestionPartModal.styles';

/**
 * Constant Imports
 */
import roundingTypes from '../../../constants/validRoundingTypes.json';

const { v1: uuidv1 } = require('uuid');

interface MetaProps {
  questionTitle: string | undefined;
  questionTags: string[];
  roundingType: string;
  archived: boolean;
  questionId: string;
  questionCondition: string[];
}

interface ModalProps {
  questions: Question[];
  selectedQuestionId: string;
  openModalInEditMode: boolean;
}

const AddQuestionModal: FC<ModalProps> = ({
  questions,
  selectedQuestionId,
  openModalInEditMode
}) => {
  const dispatch = useDispatch();

  const [showButton, setShowButton] = useState<boolean | '' | undefined>(false);
  const [isOpenAddQuestionPart, setIsOpen] = useState<boolean>(false);

  const [conditionsList, setConditionsList] = useState<ConditionListProp[]>([]);
  const [categoryList, setCategoryList] = useState<ConditionListProp[]>([]);
  const [newQuestionParts, setNewQuestionParts] = useState<QuestionPartProp[]>(
    []
  );
  const [newQuestionMeta, setNewQuestionMeta] = useState<MetaProps>({
    questionId: uuidv1(),
    questionTitle: '',
    roundingType: roundingTypes[0],
    questionTags: [],
    questionCondition: [],
    archived: false
  });
  const [isRoundingTypeOpen, setIsRoundingTypeOpen] = useState(false);
  const [isQuestionConditionsOpen, setIsQuestionConditionsOpen] =
    useState(false);
  const [isQuestionTagsOpen, setIsQuestionTagsOpen] = useState(false);

  const modalContentRef = useRef<HTMLNeuCardElement>(null);
  const manuallyChangedOptions = useRef<Record<string, boolean>>({});

  const handleQuestionPromptChange = (id: string, e: any) => {
    const { value } = e.target as HTMLNeuInputElement;
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === id) {
          const tempPart = { ...part };
          tempPart.question = value?.toString() || '';
          return tempPart;
        }
        return part;
      })
    );
  };

  const closeAnswer = (id: string) => {
    const tempNewQuestionParts = newQuestionParts.map(
      (part: QuestionPartProp) => {
        const tempPart = { ...part };
        tempPart.options = tempPart.options.filter(
          option => option.optionId !== id
        );
        return tempPart;
      }
    );
    setNewQuestionParts([...tempNewQuestionParts]);
  };

  const handleInputs = (e: any) => {
    const { name, value } = e.target as HTMLNeuInputElement;
    switch (name) {
      case 'questionTitle':
        setNewQuestionMeta({
          ...newQuestionMeta,
          questionTitle: value?.toString() || ''
        });
        break;
      default: {
        console.log('error');
      }
    }
  };

  const handleDropdownChange = useCallback(
    (value: string, name: string) => {
      switch (name) {
        case 'roundingType': {
          setNewQuestionMeta({ ...newQuestionMeta, roundingType: value });
          break;
        }
        default: {
          console.log('error');
        }
      }
    },
    [newQuestionMeta]
  );

  const handleClose = () => {
    dispatch(resetEditQuestion());
    handleModal(modalContentRef);
  };

  const questionToSave = () => {
    return {
      ...newQuestionMeta,
      questionParts:
        // Validation: Empty charge or flag must be empty string, not null
        newQuestionParts.map((part: QuestionPartProp) => ({
          ...part,
          options: part.options.map(option => ({
            ...option,
            charge: option.charge || '',
            flag: option.flag || ''
          }))
        }))
    };
  };

  const saveAndClose = () => {
    const formattedQuestionToSave = questionToSave();

    if (openModalInEditMode) {
      dispatch(updateQuestion(formattedQuestionToSave));
      // appInsights.trackEvent({
      //   name: 'Profile_Issues_Edited'
      // });
    } else {
      dispatch(addNewQuestion(formattedQuestionToSave));
    }

    handleModal(modalContentRef);
  };

  const cleanupList = (list: string[]) => {
    // Trim each item
    const mappedList = list.map(item => item.trim());

    // Remove duplicates
    const uniqueList = uniq(mappedList);

    // Order by item name, ignoring case
    const orderedList = orderBy(uniqueList, (item: string) =>
      item.toLowerCase()
    );

    // Filter empty items
    return orderedList.filter(v => !!v);
  };

  const handleChargeChange = (
    partId: string,
    optionId: string,
    charge: string
  ) => {
    setNewQuestionParts(
      newQuestionParts.map((questionPart: QuestionPartProp) => {
        if (questionPart.questionPartId === partId) {
          return {
            ...questionPart,
            options: questionPart.options.map(option => {
              if (option.optionId === optionId) {
                const tempOption = { ...option };
                tempOption.charge = charge;
                return tempOption;
              }
              return option;
            })
          };
        }
        return questionPart;
      })
    );
  };

  const addQuestionPartData = (type: string) => {
    const newPart = {
      questionPartId: uuidv1(),
      question: '',
      questionType: type,
      questionCondition: [],
      mustShow: false,
      config:
        type !== 'aggregate'
          ? {}
          : { aggregationValue: '', maxAnswers: 2, defaultValue: '' },
      options: [],
      min: '',
      max: ''
    };

    if (type === 'scale') {
      // This was hard-coded in old Orbit. Leave out for now.
      // newPart.step = 1
      newPart.min = '0';
      newPart.max = '10';
    }

    newQuestionParts.push(newPart);
    setIsOpen(false);
  };

  const addAnswer = (questionPartId: string) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === questionPartId) {
          return {
            ...part,
            options: [
              ...part.options,
              {
                title: null,
                value: null,
                optionId: uuidv1(),
                charge: 'positive',
                flag: null
              }
            ]
          };
        }
        return part;
      })
    );
  };

  const handleAddQuestionPart = () => {
    setIsOpen(true);
  };

  const removeQuestionPart = (id: string) => {
    setNewQuestionParts(
      newQuestionParts.filter(
        (part: QuestionPartProp) => part.questionPartId !== id
      )
    );
  };

  const changeOptionTitle = (
    questionPartId: string,
    optionId: string,
    title: string
  ) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === questionPartId) {
          return {
            ...part,
            options: part.options.map(option => {
              if (option.optionId === optionId) {
                const tempOption = { ...option };
                tempOption.title = title;
                if (manuallyChangedOptions.current[option.optionId] !== true) {
                  manuallyChangedOptions.current[option.optionId] = false;
                  tempOption.value = title.toLowerCase();
                }
                return tempOption;
              }
              return option;
            })
          };
        }
        return part;
      })
    );
  };

  const changeOptionValue = (
    questionPartId: string,
    optionId: string,
    value: string
  ) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === questionPartId) {
          return {
            ...part,
            options: part.options.map(option => {
              if (option.optionId === optionId) {
                const tempOption = { ...option };
                if (value !== '') {
                  tempOption.value = value;
                  manuallyChangedOptions.current[option.optionId] = true;
                } else {
                  tempOption.value = tempOption.title?.toLowerCase() || '';
                  delete manuallyChangedOptions.current[option.optionId];
                }
                return tempOption;
              }
              return option;
            })
          };
        }
        return part;
      })
    );
  };

  const addFlagToAnswer = (
    questionPartId: string,
    optionId: string,
    flag: string
  ) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) =>
        part.questionPartId === questionPartId
          ? {
              ...part,
              options: part.options.map(option =>
                option.optionId === optionId ? { ...option, flag } : option
              )
            }
          : part
      )
    );
  };

  const addConditionToQuestionPart = (
    id: string,
    conditions: ConditionListProp[]
  ) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === id) {
          return {
            ...part,
            questionCondition: conditions.map(
              (condition: ConditionListProp) => condition.value
            )
          };
        }
        return part;
      })
    );
  };

  const addToQuestionPartConfig = (id: string, key: string, value: string) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        const newPart = part;
        if (part.questionPartId === id) {
          if (key === 'aggregationValue') {
            newPart.config.aggregationValue = value;
          } else if (key === 'defaultValue') {
            newPart.config.defaultValue = value;
          } else if (!Number.isNaN(parseInt(value, 10))) {
            newPart.config.maxAnswers = parseInt(value, 10);
          }
        }
        return newPart;
      })
    );
  };

  const addTagToQuestion = (tag: ConditionListProp) => {
    const tags =
      newQuestionMeta.questionTags.findIndex((t: string) => tag.value === t) ===
      -1
        ? [...newQuestionMeta.questionTags, tag.value]
        : newQuestionMeta.questionTags.filter((t: string) => tag.value !== t);
    setNewQuestionMeta({ ...newQuestionMeta, questionTags: tags });
  };

  const addConditionToQuestion = (condition: ConditionListProp) => {
    const formattedConditions =
      newQuestionMeta.questionCondition.findIndex(
        (c: string) => condition.value === c
      ) === -1
        ? [...newQuestionMeta.questionCondition, condition.value]
        : newQuestionMeta.questionCondition.filter(
            (c: string) => condition.value !== c
          );
    setNewQuestionMeta({
      ...newQuestionMeta,
      questionCondition: formattedConditions
    });
  };

  const handleQuestionArchiveChange = (
    e: React.BaseSyntheticEvent<MouseEvent>
  ) => {
    const archive = e.target?.checked;
    // appInsights.trackEvent({
    //   name: 'Question_Archived'
    // });
    setNewQuestionMeta({ ...newQuestionMeta, archived: archive });
  };

  const updateMaxMin = (id: string, maxmin: string, e: any) => {
    const value = e.target as HTMLInputElement;
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === id) {
          const tempPart = { ...part };
          if (maxmin === 'max') {
            tempPart.max = value;
          } else if (maxmin === 'min') {
            tempPart.min = value;
          }
          return tempPart;
        }
        return part;
      })
    );
  };

  const handleMustShowFlag = (
    id: string,
    e: React.BaseSyntheticEvent<MouseEvent>
  ) => {
    setNewQuestionParts(
      newQuestionParts.map((part: QuestionPartProp) => {
        if (part.questionPartId === id) {
          const tempPart = { ...part };
          tempPart.mustShow = e.target.checked;
          return tempPart;
        }
        return part;
      })
    );
  };

  const moveItem = (from: number, to: number) => {
    // remove `from` item and store it
    const tempNewQuestionParts = newQuestionParts;
    const f = tempNewQuestionParts.splice(from, 1)[0];
    // insert stored item into position `to`
    tempNewQuestionParts.splice(to, 0, f);
    setNewQuestionParts([...tempNewQuestionParts]);
  };

  const moveUpDown = (id: string, updown: string) => {
    newQuestionParts.every((part: QuestionPartProp) => {
      if (part.questionPartId === id) {
        let to = null;
        if (updown === 'up') {
          if (newQuestionParts.indexOf(part) !== 0) {
            to = newQuestionParts.indexOf(part) - 1;
          }
        } else if (
          newQuestionParts.length - 1 !==
          newQuestionParts.indexOf(part)
        ) {
          to = newQuestionParts.indexOf(part) + 1;
        }
        if (to !== null) {
          moveItem(newQuestionParts.indexOf(part), to);
          return false;
        }
      }
      return part;
    });
  };

  const readyToSave = () => {
    const parts = newQuestionParts;
    const meta = newQuestionMeta;
    // const questionIsArchived = this.questionIsArchived

    // If archiving, bypass all validation rules
    // if (questionIsArchived) return true

    // To be valid, the question must
    // 1. Have a title
    // 2. Have at least one part
    // 3. Each part must have a question prompt
    // 4. Multi questions must have at least one answer option
    return (
      meta.questionTitle &&
      parts.length > 0 &&
      parts.every((p: QuestionPartProp) => {
        let flag;
        if (p.questionType === 'multi') {
          // Multi questions must have at least one option
          flag =
            p.question &&
            p.options.length &&
            p.options.every(option => option.value);
        } else {
          flag = p.question;
        }
        return flag;
      })
    );
  };

  useEffect(() => {
    setShowButton(readyToSave());
  }, [newQuestionParts, newQuestionMeta]);

  useEffect(() => {
    const q = questions;
    const conditionsListUncleaned: string[] = [];

    if (q && q.length > 0) {
      q.forEach(
        (question: {
          questionCondition?: string[];
          questionParts: QuestionPartProp[];
        }) => {
          // Look inside the question itself for conditions
          if (
            question.questionCondition &&
            question.questionCondition.length > 0
          ) {
            question.questionCondition.forEach(condition => {
              conditionsListUncleaned.push(condition);
            });
          }

          // Look inside each question part for conditions
          if (question.questionParts && question.questionParts.length > 0) {
            question.questionParts.forEach(part => {
              if (part.questionCondition && part.questionCondition.length > 0) {
                part.questionCondition.forEach((condition: string) => {
                  conditionsListUncleaned.push(condition.toString());
                });
              }
            });
          }
        }
      );
    }

    // Trim whitespace, filter duplicates, order by first letter case-insensitive, filter out empty
    const formattedConditions = cleanupList(conditionsListUncleaned).map(
      condition => {
        return { id: condition, value: condition };
      }
    );
    setConditionsList(formattedConditions);

    // start with patient tags & add employee ones to list
    const tagsList: string[] = qTags;

    // commenting out & just going forward with tags on list for now
    // if (q && q.length > 0) {
    //   const empQs = q.filter(
    //     (question: any = {}) => question.roundingType !== 'patient'
    //   );

    //   empQs.forEach((question: { questionTags: any[] }) => {
    //     if (question.questionTags && question.questionTags.length > 0) {
    //       question.questionTags.forEach(tag => {
    //         tagsList.push(tag);
    //       });
    //     }
    //   });
    // }

    // Trim whitespace, filter duplicates, order by first letter case-insensitive, filter out empty
    const formattedCategories = cleanupList(tagsList).map(category => {
      return { id: category, value: category };
    });
    setCategoryList(formattedCategories);
  }, [questions]);

  useEffect(() => {
    if (openModalInEditMode) {
      const arrSelectedQuestion = questions.filter(
        (val: Question) => val.questionId === selectedQuestionId
      );
      if (arrSelectedQuestion.length > 0) {
        const question = arrSelectedQuestion[0];
        setNewQuestionMeta({
          questionId: question.questionId ?? '',
          questionTitle: question.questionTitle,
          roundingType: question.roundingType,
          questionTags: question.questionTags ?? [],
          questionCondition: question.questionCondition ?? [],
          archived: question.archived ?? false
        });
        setNewQuestionParts([...question.questionParts]);
      }
    }
  }, [selectedQuestionId, openModalInEditMode, questions]);

  const addQuestionPart = () => {
    return (
      <NeuCard unit="content" className="px-0">
        <NeuList
          id="Add-Question-Part-modal"
          className="mt-0 w-100"
          color="white"
        >
          {/* TODO: This should be a dynamic list */}
          <QuestionPartTypeListItem
            unit="item"
            className="d-flex"
            role="button"
            tabIndex={0}
            onClick={() => addQuestionPartData('info')}
            onKeyDown={() => addQuestionPartData('info')}
          >
            <div className="d-flex flex-column">
              <StyledLabel
                className="neu-text-blue-60"
                style={{ fontWeight: 700, fontSize: 16 }}
              >
                Free Text
              </StyledLabel>
              <StyledLabel style={{ marginBottom: 5 }}>
                Prompt Text (no answer required)
              </StyledLabel>
            </div>
            <NeuIcon
              color="blue"
              style={{ marginLeft: 40, height: 24, width: 24 }}
            >
              add
            </NeuIcon>
          </QuestionPartTypeListItem>
          <QuestionPartTypeListItem
            unit="item"
            className="d-flex"
            role="button"
            tabIndex={0}
            onClick={() => addQuestionPartData('single')}
            onKeyDown={() => addQuestionPartData('single')}
          >
            <div className="d-flex flex-column">
              <StyledLabel
                className="neu-text-blue-60"
                style={{ fontWeight: 700, fontSize: 16 }}
              >
                Single
              </StyledLabel>
              <StyledLabel style={{ marginBottom: 5 }}>
                Select one answer
              </StyledLabel>
            </div>
            <NeuIcon
              color="blue"
              style={{ marginLeft: 145, height: 24, width: 24, marginTop: 10 }}
            >
              add
            </NeuIcon>
          </QuestionPartTypeListItem>
          <QuestionPartTypeListItem
            unit="item"
            className="d-flex"
            role="button"
            tabIndex={0}
            onClick={() => addQuestionPartData('multi')}
            onKeyDown={() => addQuestionPartData('multi')}
          >
            <div className="d-flex flex-column">
              <StyledLabel
                className="neu-text-blue-60"
                style={{ fontWeight: 700, fontSize: 16 }}
              >
                Multi
              </StyledLabel>
              <StyledLabel style={{ marginBottom: 5 }}>
                Select one or more answers
              </StyledLabel>
            </div>
            <NeuIcon
              color="blue"
              style={{ marginLeft: 80, height: 24, width: 24, marginTop: 10 }}
            >
              add
            </NeuIcon>
          </QuestionPartTypeListItem>
          <QuestionPartTypeListItem
            unit="item"
            className="d-flex"
            role="button"
            tabIndex={0}
            onClick={() => addQuestionPartData('scale')}
            onKeyDown={() => addQuestionPartData('scale')}
          >
            <div className="d-flex flex-column">
              <StyledLabel
                className="neu-text-blue-60"
                style={{ fontWeight: 700, fontSize: 16 }}
              >
                Scale
              </StyledLabel>
              <StyledLabel>Select a number from a range</StyledLabel>
              <StyledLabel style={{ marginBottom: 5 }}>
                e.g. 6 out of 10
              </StyledLabel>
            </div>
            <NeuIcon
              color="blue"
              style={{ marginLeft: 67, height: 24, width: 24, marginTop: 15 }}
            >
              add
            </NeuIcon>
          </QuestionPartTypeListItem>
          <QuestionPartTypeListItem
            unit="item"
            className="d-flex"
            role="button"
            tabIndex={0}
            onClick={() => addQuestionPartData('text')}
            onKeyDown={() => addQuestionPartData('text')}
          >
            <div className="d-flex flex-column">
              <StyledLabel
                className="neu-text-blue-60"
                style={{ fontWeight: 700, fontSize: 16 }}
              >
                Text
              </StyledLabel>
              <StyledLabel style={{ marginBottom: 5 }}>
                Enter free form text
              </StyledLabel>
            </div>
            <NeuIcon
              color="blue"
              style={{ marginLeft: 130, height: 24, width: 24, marginTop: 10 }}
            >
              add
            </NeuIcon>
          </QuestionPartTypeListItem>
          <QuestionPartTypeListItem
            unit="item"
            className="d-flex"
            role="button"
            tabIndex={0}
            onClick={() => addQuestionPartData('aggregate')}
            onKeyDown={() => addQuestionPartData('aggregate')}
          >
            <div className="d-flex flex-column">
              <StyledLabel
                className="neu-text-blue-60"
                style={{ fontWeight: 700, fontSize: 16 }}
              >
                Aggregate
              </StyledLabel>
              <StyledLabel style={{ marginBottom: 0 }}>
                Select from answers of other
              </StyledLabel>
              <StyledLabel style={{ marginBottom: 5 }}>questions</StyledLabel>
            </div>
            <NeuIcon
              color="blue"
              style={{ marginLeft: 70, height: 24, width: 24, marginTop: 20 }}
            >
              add
            </NeuIcon>
          </QuestionPartTypeListItem>
        </NeuList>
      </NeuCard>
    );
  };

  return (
    <AddQuestionCard
      id="Add-Question-Modal"
      unit="card"
      className="show-modal"
      ref={modalContentRef}
    >
      <CardHeader unit="header">
        <StyledLabel style={{ color: '#093865', marginLeft: 1 }}>
          {openModalInEditMode ? 'Edit Question' : 'New Question'}
        </StyledLabel>
        <NeuButton
          aria-label="close panel"
          kind="tertiary"
          icon="close"
          size="small"
          onClick={handleClose}
        >
          Close
        </NeuButton>
      </CardHeader>
      <ModalCardContent unit="content">
        <CardContentContainer className="d-flex justify-content-between w-100">
          <RoundingTypeDropdownContainer>
            {/* TODO: Can use custom rounding type dropdown */}
            <InputHeading>Rounding Type</InputHeading>
            <Dropdown
              classes="ps-0 pt-0"
              name="roundingType"
              open={isRoundingTypeOpen}
              placeholder={formatRoundingType(newQuestionMeta.roundingType)}
              styles={{ minWidth: 'fit-content' }}
              width="120%"
              setOpen={setIsRoundingTypeOpen}
            >
              {roundingTypes.map((type: string) => (
                <div
                  key={type}
                  className={`dropdown-hover-item${
                    newQuestionMeta.roundingType === type.toLowerCase()
                      ? ' selected'
                      : ''
                  }`}
                  aria-selected={
                    newQuestionMeta.roundingType === type.toLowerCase()
                  }
                  role="option"
                  tabIndex={0}
                  onClick={() =>
                    handleDropdownChange(type.toLowerCase(), 'roundingType')
                  }
                  onKeyDown={() =>
                    handleDropdownChange(type.toLowerCase(), 'roundingType')
                  }
                >
                  {formatRoundingType(type)}
                </div>
              ))}
            </Dropdown>
          </RoundingTypeDropdownContainer>
          <QuestionTitleInput
            id="Question-Title-Input"
            autocomplete="off"
            autoCorrect="off"
            label="Question Title (e.g. Pain Level)"
            name="questionTitle"
            required
            spellCheck
            value={newQuestionMeta.questionTitle || ''}
            onNeuChange={handleInputs}
          />
        </CardContentContainer>
        <div className="my-1">
          <div className="pt-1 pb-2">
            {newQuestionMeta.questionCondition.length > 0 &&
              [...newQuestionMeta.questionCondition].sort().map(condition => {
                return (
                  <span
                    key={condition}
                    aria-label="condition-chip"
                    role="button"
                    tabIndex={0}
                    onClick={() => addConditionToQuestion({ value: condition })}
                    onKeyDown={() =>
                      addConditionToQuestion({ value: condition })
                    }
                  >
                    <BlueChip name={condition} />
                  </span>
                );
              })}
          </div>
          {/* TODO: Needs to be a custom dropdown */}
          <InputHeading>Question Conditions</InputHeading>
          <Dropdown
            id="questionConditions"
            data-testid="question-conditions-dropdown"
            classes="ps-0 pt-0"
            name="questionConditions"
            closeOnClick={false}
            open={isQuestionConditionsOpen}
            placeholder="Select one or more..."
            setOpen={setIsQuestionConditionsOpen}
          >
            {[...conditionsList]
              .sort((a, b) =>
                a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1
              )
              .map((condition: ConditionListProp) => {
                const selected = newQuestionMeta.questionCondition?.includes(
                  condition.value
                );
                return (
                  <div
                    key={condition.id}
                    aria-selected={selected}
                    className={`dropdown-hover-item ${
                      selected ? 'selected' : ''
                    }`}
                    role="option"
                    tabIndex={0}
                    onClick={() => addConditionToQuestion(condition)}
                    onKeyDown={() => addConditionToQuestion(condition)}
                  >
                    <div>
                      {condition.value}
                      {selected && (
                        <NeuIcon className="custom-checkmark" color="blue">
                          check
                        </NeuIcon>
                      )}
                    </div>
                  </div>
                );
              })}
          </Dropdown>
        </div>
        <div className="d-flex flex-column my-1">
          <InputHeading>Question Tags</InputHeading>
          <div className="pt-1 pb-2">
            {newQuestionMeta.questionTags.length > 0 &&
              [...newQuestionMeta.questionTags].sort().map(tag => {
                return (
                  <span
                    key={tag}
                    aria-label="tag-chip"
                    role="button"
                    tabIndex={0}
                    onClick={() => addTagToQuestion({ value: tag })}
                    onKeyDown={() => addTagToQuestion({ value: tag })}
                  >
                    <BlueChip name={tag} />
                  </span>
                );
              })}
          </div>
          {/* TODO: Needs to be a custom dropdown */}
          <Dropdown
            id="questionTags"
            classes="ps-0 pt-0"
            name="questionTags"
            closeOnClick={false}
            open={isQuestionTagsOpen}
            placeholder="Select one or more..."
            setOpen={setIsQuestionTagsOpen}
          >
            {[...categoryList]
              .sort((a, b) =>
                a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1
              )
              .map((category: ConditionListProp) => {
                const selected = newQuestionMeta.questionTags?.includes(
                  category.value
                );
                return (
                  <div
                    key={category.id}
                    aria-selected={selected}
                    className={`dropdown-hover-item ${
                      selected ? 'selected' : ''
                    }`}
                    role="option"
                    tabIndex={0}
                    onClick={() => addTagToQuestion(category)}
                    onKeyDown={() => addTagToQuestion(category)}
                  >
                    <div>
                      {category.value}
                      {selected && (
                        <NeuIcon className="custom-checkmark" color="blue">
                          check
                        </NeuIcon>
                      )}
                    </div>
                  </div>
                );
              })}
          </Dropdown>
        </div>
        {newQuestionParts.map((part: QuestionPartProp) => {
          return (
            <QuestionPart
              editQuestion={openModalInEditMode}
              part={part}
              manuallyChangedOptions={manuallyChangedOptions}
              conditionsList={conditionsList}
              moveUpDown={moveUpDown}
              removeQuestionPart={removeQuestionPart}
              handleQuestionPromptChange={handleQuestionPromptChange}
              addConditionToQuestionPart={addConditionToQuestionPart}
              addToQuestionPartConfig={addToQuestionPartConfig}
              handleMustShowFlag={handleMustShowFlag}
              updateMaxMin={updateMaxMin}
              changeOptionTitle={changeOptionTitle}
              changeOptionValue={changeOptionValue}
              addFlagToAnswer={addFlagToAnswer}
              handleChargeChange={handleChargeChange}
              closeAnswer={closeAnswer}
              addAnswer={addAnswer}
              key={part.questionPartId}
            />
          );
        })}
        <div
          style={{
            height: 30
          }}
        >
          <NeuButton
            id="Add-Question-Question-Part-Button"
            color="blue"
            size="small"
            onClick={() => handleAddQuestionPart()}
          >
            Add Question Part
          </NeuButton>
        </div>
      </ModalCardContent>
      <ModalFooter unit="footer" className="justify-between ms-3 me-1 pt-3">
        <div className="d-flex ms-1">
          {openModalInEditMode ? (
            <div>
              <NeuCheckbox
                checked={newQuestionMeta.archived}
                label="Archive"
                onClick={e => handleQuestionArchiveChange(e)}
              />
            </div>
          ) : null}
        </div>
        <div>
          <NeuButton
            id="Unlisted-Patient-Begin-Round-Button"
            color="blue"
            disabled={!showButton}
            size="small"
            onClick={showButton ? saveAndClose : undefined}
          >
            Save Question
          </NeuButton>
        </div>
      </ModalFooter>
      {isOpenAddQuestionPart && (
        <AddQuestionPartCard id="Add-Question-Part-Modal" unit="card">
          {addQuestionPart()}
        </AddQuestionPartCard>
      )}
    </AddQuestionCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    questions: state.TemplateReducer.allQuestions,
    units: state.ConfigReducer.units,
    selectedQuestionId: state.TemplateReducer.selectedQuestionId,
    openModalInEditMode: state.TemplateReducer.editQuestion
  };
};

export default connect(mapReduxStateToProps)(AddQuestionModal);
